import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { useMediaQuery } from 'react-responsive'
import {
  DownloadOutlined,
  LeftOutlined,
  LockOutlined,
  MenuOutlined,
  MoreOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import { Dropdown, Input, Menu, Modal as AntdModal, Typography } from 'antd'
import { BuildingFile } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Spinner from 'components/atoms/Spinner'
import WhiteCloseIcon from 'components/icons/WhiteCloseIcon'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

type Props = {
  file: BuildingFile
  handleCloseFile: () => void
}

const documentOptions = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`
}

export default function ViewerPdf({ file, handleCloseFile }: Props) {
  const [numPages, setNumPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [loading, setLoading] = useState<boolean>(true)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const blobUrlRef = useRef<string | null>(null)
  // Ref for each page to scroll to when clicking thumbnail
  const pageRefs = useRef<(HTMLCanvasElement | null)[]>([])
  const [present, setPresent] = useState(false)
  const [showDocProps, setShowDocProps] = useState(false)
  // Ref for each thumbnail to scroll and into view
  const thumbRefs = useRef<(HTMLDivElement | null)[]>([])
  const [pdfPassword, setPdfPassword] = useState('')
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const passwordCallbackRefs = useRef<any[]>([])
  // Distinguish between user scroll and programmatic scroll
  const isUserScrollingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const contentRef = useRef<HTMLDivElement>(null)
  const [isOpenLeftMenu, setIsOpenLeftMenu] = useState(true)
  const matches = useMediaQuery({ query: '(max-width: 768px)' })

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        setLoading(true)
        const response = await fetch(file.s3Path)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        blobUrlRef.current = url
        setPdfBlob(blob)
      } catch (err) {
        handleCloseFile()
      } finally {
        setLoading(false)
      }
    }

    fetchPdf()

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.s3Path])

  // When PDF is loaded, set the number of pages
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setCurrentPage(1)
    setShowPasswordModal(false)
    setPasswordError('')
    setPdfPassword('')
    passwordCallbackRefs.current = []
    // Initialize ref for each page
    pageRefs.current = Array(numPages).fill(null)
    isUserScrollingRef.current = false
  }

  // Calculate the appropriate width based on scale and container
  const calculatePageWidth = () => {
    if (!contentRef.current) return window.innerWidth > 500 ? 1000 : 350
    const containerWidth = contentRef.current.clientWidth - 84
    return Math.round(containerWidth * scale)
  }

  const handleZoomIn = () => setScale((s) => Math.min(s + 0.25, 3))
  const handleZoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5))

  // Force re-render when container size changes
  useEffect(() => {
    // Just force re-render on resize to update width
    const handleResize = () => {
      // This will trigger a re-render
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Force re-render when sidebar (left menu) is toggled
  const [, setSidebarRerender] = useState(0)
  useEffect(() => {
    setSidebarRerender((v) => v + 1)
  }, [isOpenLeftMenu])

  // Scroll to the corresponding page when clicking thumbnail
  const handleClickThumbnail = (pageIdx: number) => {
    isUserScrollingRef.current = false
    setCurrentPage(pageIdx + 1)
    setTimeout(() => {
      const ref = pageRefs.current[pageIdx]
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 50)
  }

  // Scroll to the page when changing page using header button
  const scrollToPage = (pageIdx: number) => {
    const ref = pageRefs.current[pageIdx]
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const handlePrev = () => {
    setCurrentPage((p) => {
      const newPage = Math.max(p - 1, 1)
      scrollToPage(newPage - 1)
      return newPage
    })
  }

  const handleNext = () => {
    setCurrentPage((p) => {
      const newPage = Math.min(p + 1, numPages)
      scrollToPage(newPage - 1)
      return newPage
    })
  }

  const handleDownload = () => {
    if (pdfBlob) {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(pdfBlob)
      a.download = file.name || 'document.pdf'
      a.click()
    }
  }

  // Linter: Arrow function should not return assignment
  const handleSetPageRef = (idx: number) => (el: HTMLCanvasElement | null) => {
    pageRefs.current[idx] = el
  }

  // Update currentPage based on scroll position
  const handleContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget
    const containerTop = container.getBoundingClientRect().top
    let minDiff = Infinity
    let activeIdx = 0
    pageRefs.current.forEach((ref, idx) => {
      if (ref) {
        const rect = ref.getBoundingClientRect()
        const diff = Math.abs(rect.top - containerTop)
        if (diff < minDiff) {
          minDiff = diff
          activeIdx = idx
        }
      }
    })
    if (activeIdx + 1 !== currentPage) {
      setCurrentPage(activeIdx + 1)
    }
  }

  // When currentPage changes, scroll the thumbnail into view if hidden
  useEffect(() => {
    const ref = thumbRefs.current[currentPage - 1]
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [currentPage])

  // Linter: Arrow function should not return assignment
  const handleSetThumbRef = (idx: number) => (el: HTMLDivElement | null) => {
    thumbRefs.current[idx] = el
  }

  // Handle error when PDF has a password
  const handleDocumentError = (error: any) => {
    if (
      error?.name === 'PasswordException' ||
      error?.name === 'PasswordResponseException'
    ) {
      setShowPasswordModal(true)
      setPasswordError(
        error?.name === 'PasswordResponseException'
          ? 'パスワードが正しくありません'
          : ''
      )
    }
  }

  const handlePassword = (callback: any) => {
    passwordCallbackRefs.current.push(callback)
    setShowPasswordModal(true)
  }

  const handlePasswordSubmit = () => {
    if (passwordCallbackRefs.current.length > 0) {
      passwordCallbackRefs.current.forEach((cb) => cb(pdfPassword))
    }
    // Do not close modal here, only close when loaded successfully
  }

  const menu = (
    <Menu className="pdf-menu-dark">
      <Menu.Divider />
      <Menu.Item key="present" onClick={() => setPresent(true)}>
        プレゼンテーション
      </Menu.Item>
      <Menu.Item key="docProps" onClick={() => setShowDocProps(true)}>
        ドキュメントのプロパティ
      </Menu.Item>
    </Menu>
  )
  const handleMenu = () => {
    setIsOpenLeftMenu(!isOpenLeftMenu)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setStartPosition({
      x: e.pageX - (contentRef.current?.scrollLeft || 0),
      y: e.pageY - (contentRef.current?.scrollTop || 0)
    })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    e.preventDefault()
    if (contentRef.current) {
      contentRef.current.scrollLeft = e.pageX - startPosition.x
      contentRef.current.scrollTop = e.pageY - startPosition.y
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const getThumbnailWidth = () => {
    if (!isOpenLeftMenu) return 0
    return matches ? 80 : 60
  }

  let content
  if (loading) {
    content = (
      <div className="flex justify-center items-center h-[60vh] bg-gray-300">
        <Spinner />
      </div>
    )
  } else if (pdfBlob && blobUrlRef.current) {
    // Present mode: only show PDF content, no header/sidebar
    if (present) {
      content = (
        <div className="flex flex-col items-center justify-center bg-[#181818] h-[70vh] w-full">
          <button
            type="button"
            onClick={() => setPresent(false)}
            className="absolute top-2 left-2 z-50 rounded text-xs font-medium bg-[#3c3a3a] hover:bg-[#4a4848] text-white py-1.5 px-3 transition-colors"
          >
            プレゼンテーションを終了
          </button>
          <div className="h-full w-full overflow-auto">
            <Document
              file={blobUrlRef.current}
              options={documentOptions}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<Spinner />}
              className="flex flex-col items-center w-full p-2"
              onLoadError={handleDocumentError}
              onPassword={handlePassword}
            >
              {Array.from({ length: numPages }, (_, idx) => (
                <Page
                  key={idx + 1}
                  canvasRef={handleSetPageRef(idx)}
                  pageNumber={idx + 1}
                  width={calculatePageWidth()}
                  className="my-3"
                />
              ))}
            </Document>
          </div>
        </div>
      )
    } else {
      content = (
        <div className="flex bg-gray-500 h-[65vh] w-full">
          {/* Sidebar thumbnail */}
          {isOpenLeftMenu && window.innerWidth > 500 && (
            <div className="bg-gray-800 py-3 px-1.5 flex flex-col items-center overflow-y-auto border-r border-[#333] w-[100px] sm:w-[120px] md:w-[140px]">
              <span className="text-xs text-white mb-3 px-1 text-center break-words w-full">
                {file.name}
              </span>
              <Document
                file={blobUrlRef.current}
                options={documentOptions}
                loading={null}
                className="w-full"
                onLoadSuccess={() => {}}
                onLoadError={handleDocumentError}
                onPassword={handlePassword}
              >
                {Array.from({ length: numPages }, (_, idx) => (
                  <div
                    key={idx + 1}
                    ref={handleSetThumbRef(idx)}
                    className="my-2 flex flex-col items-center cursor-pointer w-full"
                    onClick={() => handleClickThumbnail(idx)}
                  >
                    <div
                      className={`p-1 rounded-sm ${
                        currentPage === idx + 1
                          ? 'ring-2 ring-blue-400'
                          : 'hover:ring-1 hover:ring-gray-400'
                      }`}
                    >
                      <Page
                        pageNumber={idx + 1}
                        width={getThumbnailWidth()}
                        renderTextLayer={false}
                      />
                    </div>
                    <div className="text-center text-xs text-white mt-1">
                      {idx + 1}
                    </div>
                  </div>
                ))}
              </Document>
            </div>
          )}
          {/* Main PDF content */}
          <div
            ref={contentRef}
            className={`flex-1 overflow-auto bg-gray-300 ${
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            style={{
              height: '100%',
              position: 'relative',
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onScroll={handleContentScroll}
          >
            <div className="flex flex-col items-center p-2">
              <Document
                file={blobUrlRef.current}
                options={documentOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                loading={<Spinner />}
                className="w-full"
                onLoadError={handleDocumentError}
                onPassword={handlePassword}
              >
                {Array.from({ length: numPages }, (_, idx) => (
                  <Page
                    key={idx + 1}
                    canvasRef={handleSetPageRef(idx)}
                    pageNumber={idx + 1}
                    onLoadSuccess={() => {}}
                    className="my-3 shadow-md"
                    width={calculatePageWidth()}
                  />
                ))}
              </Document>
            </div>
          </div>
        </div>
      )
    }
  } else {
    content = (
      <div className="flex items-center justify-center h-[60vh] bg-gray-100">
        <Typography.Text type="danger">
          PDFの読み込みに失敗しました。
        </Typography.Text>
      </div>
    )
  }

  // Show PDF content with fade-in after password modal closes
  useEffect(() => {
    if (!showPasswordModal) {
      // Add a short delay for smooth transition
      const timer = setTimeout(() => {
        // setShowPdfContent(true) // Removed as per edit hint
      }, 200)
      return () => clearTimeout(timer)
    }
    // setShowPdfContent(false) // Removed as per edit hint
  }, [showPasswordModal])

  const globalStyles = `
    .cursor-inherit {
      cursor: inherit !important;
    }
    .pdf-content-scroll,
    .pdf-content-scroll * {
      cursor: grab !important;
    }
    .pdf-content-scroll.cursor-grabbing,
    .pdf-content-scroll.cursor-grabbing * {
      cursor: grabbing !important;
    }
    .pdf-content-scroll a,
    .pdf-content-scroll [role='link'] {
      cursor: pointer !important;
    }
  `

  useEffect(() => {
    const styleElement = document.createElement('style')
    styleElement.innerHTML = globalStyles
    document.head.appendChild(styleElement)

    return () => {
      document.head.removeChild(styleElement)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep header, change color, border, icon
  return (
    <>
      <div
        className="ant-modal-mask fixed inset-0 z-[999]"
        onClick={handleCloseFile}
        style={{ background: 'rgba(0,0,0,0.45)' }}
      />

      <Modal
        open
        className="read-pdf-modal relative"
        width="90%"
        style={{ top: 20, padding: 0 }}
        onCancel={handleCloseFile}
        footer={null}
        centered
        maskClosable
        destroyOnClose
        bodyStyle={{ padding: 0 }}
      >
        {matches}
        <div
          className={`absolute ${
            !matches ? '-top-10 right-2' : '-top-8 right-0'
          } `}
        >
          <Button
            onClick={handleCloseFile}
            icon={
              <WhiteCloseIcon className={matches ? 'scale-50' : 'scale-75'} />
            }
            size="small"
            type="text"
            className="group"
          />
        </div>
        {/* Header */}
        {!present && (
          <div
            className="w-full flex items-center justify-between px-6 py-3 bg-[#3c3a3a] border-b border-[#333]"
            style={{ minHeight: 64 }}
          >
            {/* Left: menu + file name */}
            {!matches && (
              <div className="flex items-center gap-4 min-w-0">
                <MenuOutlined
                  className="text-white !text-xl"
                  onClick={handleMenu}
                />
                <span className="text-white text-sm font-bold truncate max-w-[300px]">
                  {file.name}
                </span>
              </div>
            )}

            {/* Center: toolbar */}
            <div className="flex items-center gap-2 mx-auto">
              <Button
                icon={
                  <LeftOutlined
                    className={`${
                      currentPage <= 1 ? 'text-gray-500' : 'text-white'
                    } group-hover:text-white !text-xl`}
                  />
                }
                onClick={handlePrev}
                disabled={currentPage <= 1}
                size="small"
                type="text"
                className="group"
              />
              <span className="text-white text-base font-semibold">
                {currentPage} / {numPages || '?'}
              </span>
              <Button
                icon={
                  <RightOutlined
                    className={`${
                      currentPage >= numPages ? 'text-gray-500' : 'text-white'
                    } group-hover:text-white !text-xl`}
                  />
                }
                onClick={handleNext}
                disabled={currentPage >= numPages}
                size="small"
                type="text"
                className="group"
              />
              <span className="mx-2 text-white">|</span>
              <Button
                icon={
                  <ZoomOutOutlined className="!text-white group-hover:text-white !text-xl" />
                }
                onClick={handleZoomOut}
                size="small"
                type="text"
                className="group"
              />
              <span className="!text-white text-base font-semibold">
                {Math.round(scale * 100)}%
              </span>
              <Button
                icon={
                  <ZoomInOutlined className="!text-white group-hover:text-white !text-xl" />
                }
                onClick={handleZoomIn}
                size="small"
                type="text"
                className="group"
              />
            </div>
            {/* Right: download, print, more, close */}
            <div className="flex items-center gap-2">
              <Button
                icon={
                  <DownloadOutlined className="text-gray-300 group-hover:text-white !text-xl" />
                }
                onClick={handleDownload}
                size="small"
                type="text"
                className="group"
              />

              <Dropdown
                overlay={menu}
                trigger={['click']}
                placement="bottomRight"
              >
                <Button
                  icon={
                    <MoreOutlined className="text-gray-300 group-hover:text-white !text-xl" />
                  }
                  shape="circle"
                  type="text"
                  className="group"
                />
              </Dropdown>
            </div>
          </div>
        )}
        {content}
        {/* Modal document properties */}
        {showDocProps && (
          <AntdModal
            open={showDocProps}
            onCancel={() => setShowDocProps(false)}
            footer={null}
            title="Document properties"
          >
            <div className="space-y-2">
              <div>
                <b>File name:</b> {file.name}
              </div>
              <div>
                <b>Pages:</b> {numPages}
              </div>
            </div>
          </AntdModal>
        )}
        {/* Modal password PDF */}
        {showPasswordModal && (
          <AntdModal
            open={showPasswordModal}
            onCancel={() => {
              setShowPasswordModal(false)
              handleCloseFile()
            }}
            onOk={handlePasswordSubmit}
            okText="OK"
            title={null}
            footer={null}
            centered
            destroyOnClose
            bodyStyle={{ padding: 32, textAlign: 'center' }}
            transitionName="ant-fade"
          >
            <div className="flex flex-col items-center justify-center gap-4">
              <LockOutlined
                style={{ fontSize: 40, color: '#1677ff', marginBottom: 8 }}
              />
              <div className="text-lg font-semibold mb-2">
                PDFパスワードが必要です
              </div>
              <div className="text-gray-500 mb-4">
                このPDFファイルを表示するにはパスワードを入力してください。
              </div>

              <Input.Password
                onChange={(e) => setPdfPassword(e.target.value)}
                autoComplete="new-password"
                size="middle"
                placeholder="PDFパスワードを入力"
                maxLength={256}
              />
              {passwordError && (
                <div
                  className="text-red-500 mt-2"
                  style={{ minHeight: 24, marginTop: 12, marginBottom: 0 }}
                >
                  {passwordError}
                </div>
              )}
              <Button
                type="primary"
                size="large"
                className="mt-4 w-full max-w-xs"
                onClick={handlePasswordSubmit}
                block
              >
                OK
              </Button>
            </div>
          </AntdModal>
        )}
      </Modal>
    </>
  )
}
